import { ChangeDetectionStrategy, Component, effect, inject } from '@angular/core';
import {Store} from '@ngrx/store';
import {AppState} from '../store';
import {TenantInfo} from '../models/tenant.model';
import {UserPermissions} from '../models/user-permissions.model';
import {selectTenantInfo, selectUserPermissions} from '../store/selectors';
import {environment} from '../../environments/environment';
import { toSignal } from '@angular/core/rxjs-interop';

interface FacebookIntegrationData {
  tenant: TenantInfo,
  permissions: UserPermissions

}

/**
 *
 * This non-visual component loads the Rewardful integration scripts into the browser.
 *
 * Rewardful is a Stripe Affiliate Program platform, and we use it in two different ways.
 *
 * First, we run our own platform affiliate program with it - anyone bringing new paying course creators to the platform gets a commission.
 *
 * Second, we allow course creators run their own Stripe affiliate program, to bring affiliates to their tenant websites.
 *
 * The integration follows this documentation - https://app.getrewardful.com/setup/code?platform=javascript_browser
 *
 */

@Component({
    selector: "rewardful-integration",
    template: "",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RewardfulIntegrationComponent{
  private store = inject<Store<AppState>>(Store);
  private readonly tenant = toSignal(this.store.select(selectTenantInfo));
  private readonly permissions = toSignal(this.store.select(selectUserPermissions));

  constructor() {
    effect(() => {
      const tenant = this.tenant();
      const permissions = this.permissions();

      if (tenant && permissions) {
        this.initRewardfulIntegration({tenant, permissions});
      }
    });
  }

  private initRewardfulIntegration(data: FacebookIntegrationData) {

    const {permissions, tenant} = data;

    // if the user is an admin, then run the platform affiliate program
    if (permissions.isAdmin) {
      console.log("Loading platform affiliate scripts ");
      this.loadRewardfulScripts(environment.rewardful.platformAffiliateId);
    }

  }

  private loadRewardfulScripts(rewardFulId:string) {
    this.ensureRewardfulQueue();

    /// load the Google Analytics async script
    var script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://r.wdfl.co/rw.js`;
    script.setAttribute("data-rewardful", rewardFulId);
    var head = document.getElementsByTagName('head')[0];
    head.appendChild(script);

  }

  private ensureRewardfulQueue() {
    const win = window as any;
    if (!win.rewardful) {
      const rewardful = function (...args: any[]) {
        (rewardful as any).q = (rewardful as any).q || [];
        (rewardful as any).q.push(args);
      } as any;
      win._rwq = 'rewardful';
      win.rewardful = rewardful;
    }
  }


}

